import React, { useCallback, useState } from 'react';
import { ChevronDown, ChevronRight, CheckCircle2, Tag, FileWarning, Archive, EyeOff, Eye } from 'lucide-react';
import { EnhancedDeal, ProjectPLBucket } from '../../types';
import {
  PL_COLLECTED_REVENUE_MARK,
  PL_COLLECTED_REVENUE_NOTE,
  ProjectPLRow as ProjectPLRowData,
} from '../../utils/projectPL';
import ProjectPLBudgetPanel from './ProjectPLBudgetPanel';

interface ProjectPLRowProps {
  row: ProjectPLRowData;
  formatCurrency: (n: number) => string;
  formatCurrencyFull: (n: number) => string;
  expanded: boolean;
  onToggle: () => void;
  onExpenseBucketChange: (expenseId: string, bucket: ProjectPLBucket | null) => Promise<void>;
  onFinalize: () => Promise<void>;
  onReopen?: () => Promise<void>;
  /** Toggle whether the project counts towards dashboard metrics. */
  onToggleExcluded: (excluded: boolean, reason?: string) => Promise<void>;
  /** Move a QB payment to another project or detach. */
  onReassignPayment?: (paymentId: string, dealId: string | null) => Promise<void>;
  /** Move a QB expense to another project, overhead pool, or detach. */
  onReassignExpense?: (expenseId: string, target: string) => Promise<void>;
  /** Available deals for the inline payment-reassignment picker. */
  reassignableDeals?: EnhancedDeal[];
  readOnly?: boolean;
}

function marginPctClass(pct: number): string {
  if (pct >= 35) return 'sa-ppl-pct-good';
  if (pct >= 20) return 'sa-ppl-pct-ok';
  return 'sa-ppl-pct-bad';
}

function variancePctClass(pct: number, budget: number): string {
  if (budget <= 0) return 'sa-ppl-pct-neutral';
  if (Math.abs(pct) <= 5) return 'sa-ppl-pct-good';
  if (Math.abs(pct) <= 15) return 'sa-ppl-pct-ok';
  return 'sa-ppl-pct-bad';
}

const ProjectPLRow: React.FC<ProjectPLRowProps> = ({
  row,
  formatCurrencyFull,
  expanded,
  onToggle,
  onExpenseBucketChange,
  onFinalize,
  onReopen,
  onToggleExcluded,
  onReassignPayment,
  onReassignExpense,
  reassignableDeals,
  readOnly,
}) => {
  const [busy, setBusy] = useState(false);

  const handleFinalize = useCallback(async () => {
    setBusy(true);
    try {
      await onFinalize();
    } finally {
      setBusy(false);
    }
  }, [onFinalize]);

  const handleReopen = useCallback(async () => {
    if (!onReopen) return;
    setBusy(true);
    try {
      await onReopen();
    } finally {
      setBusy(false);
    }
  }, [onReopen]);

  const handleToggleExcluded = useCallback(async () => {
    if (!row.excluded) {
      const reason =
        typeof window !== 'undefined'
          ? window.prompt(
              'Why exclude this project? (optional — shown next to the row)\n\nExcluded projects are dropped from KPIs and overhead allocation but stay accessible via the Excluded filter.',
              row.excludedReason || '',
            )
          : '';
      if (reason === null) return;
      setBusy(true);
      try {
        await onToggleExcluded(true, reason.trim() || undefined);
      } finally {
        setBusy(false);
      }
      return;
    }
    setBusy(true);
    try {
      await onToggleExcluded(false);
    } finally {
      setBusy(false);
    }
  }, [row.excluded, row.excludedReason, onToggleExcluded]);

  const handleSummaryClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if ((e.target as HTMLElement).closest('button, select, a, input, textarea')) return;
      onToggle();
    },
    [onToggle],
  );

  return (
    <div className={`sa-ppl-row ${expanded ? 'expanded' : ''} ${row.pmFinalizedAt ? 'finalized' : ''} ${row.excluded ? 'excluded' : ''}`}>
      <div
        className="sa-ppl-row-summary"
        onClick={handleSummaryClick}
      >
        <button
          type="button"
          className="sa-ppl-row-expand"
          onClick={onToggle}
          aria-expanded={expanded}
          aria-label={expanded ? `Collapse ${row.dealTitle}` : `Expand ${row.dealTitle}`}
        >
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </button>
        <span className="sa-ppl-row-project">
          <span className="sa-ppl-row-title">{row.dealTitle}</span>
          <span className="sa-ppl-row-sub">
            {row.contactName}
            {row.pipelineStageName ? ` · ${row.pipelineStageName}` : ''}
          </span>
        </span>
        <span
          className="sa-ppl-row-cell sa-ppl-num"
          title={
            row.revenueSource === 'collected'
              ? PL_COLLECTED_REVENUE_NOTE
              : `Deal value ${formatCurrencyFull(row.dealValue)} + design fee ${formatCurrencyFull(row.designFeeRevenue)}`
          }
        >
          {formatCurrencyFull(row.revenue)}
          {row.revenueSource === 'collected' && (
            <span className="sa-ppl-collected-mark" aria-label="Collected revenue">{PL_COLLECTED_REVENUE_MARK}</span>
          )}
        </span>
        <span className="sa-ppl-row-cell sa-ppl-num" title={`Direct job costs ${formatCurrencyFull(row.directCost)} (QB + approved designer invoices) + allocated overhead ${formatCurrencyFull(row.allocatedOverhead)}`}>
          {formatCurrencyFull(row.actualCost)}
        </span>
        <span
          className={`sa-ppl-row-cell sa-ppl-num ${row.hasProposal ? '' : 'sa-ppl-row-cell-muted'}`}
          title={row.hasProposal ? 'From the latest proposal (raw cost, pre-margin).' : 'No proposal builder data — variance vs budget unavailable.'}
        >
          {row.hasProposal ? formatCurrencyFull(row.budget) : <span className="sa-ppl-no-proposal">No proposal</span>}
        </span>
        <span className="sa-ppl-row-cell sa-ppl-num">
          <strong>{formatCurrencyFull(row.grossMargin)}</strong>
          <span className={`sa-ppl-row-pct ${marginPctClass(row.grossMarginPct)}`}>
            {row.grossMarginPct.toFixed(0)}%
          </span>
        </span>
        <span className={`sa-ppl-row-cell sa-ppl-num ${variancePctClass(row.varianceVsBudgetPct, row.budget)}`}>
          {row.budget > 0
            ? `${row.varianceVsBudget >= 0 ? '+' : ''}${row.varianceVsBudgetPct.toFixed(0)}%`
            : <span className="sa-ppl-no-proposal">—</span>}
        </span>
        <span className="sa-ppl-row-status">
          {row.unassignedExpenseCount > 0 && (
            <span className="sa-ppl-chip sa-ppl-chip-warn" title="Expenses without a P&L bucket — pick one in the drill-in.">
              <Tag size={11} /> {row.unassignedExpenseCount} to sort
            </span>
          )}
          {!row.hasProposal && (
            <span className="sa-ppl-chip sa-ppl-chip-muted" title="No proposal builder data found for this deal.">
              <FileWarning size={11} /> No proposal
            </span>
          )}
          {row.readyToFinalize && !row.pmFinalizedAt && (
            <span className="sa-ppl-chip sa-ppl-chip-ok" title="Cash collected and all expenses categorized — ready to finalize.">
              <CheckCircle2 size={11} /> Ready
            </span>
          )}
          {row.pmFinalizedAt && (
            <span className="sa-ppl-chip sa-ppl-chip-archive" title={`Finalized ${row.pmFinalizedAt.toLocaleDateString()}`}>
              <Archive size={11} /> Finalized
            </span>
          )}
          {row.plReconciliationTotal !== 0 && (
            <span
              className="sa-ppl-chip sa-ppl-chip-muted"
              title={
                row.plReconciliations.length
                  ? row.plReconciliations
                    .map(line => `${line.amount >= 0 ? '+' : '−'}$${Math.abs(line.amount).toLocaleString()}: ${line.title}${line.reason ? ` — ${line.reason}` : ''}`)
                    .join('\n')
                  : 'P&L-only adjustment. Does not change the CRM deal value or create a change order.'
              }
            >
              P&amp;L adj {row.plReconciliationTotal >= 0 ? '+' : '−'}
              {formatCurrencyFull(Math.abs(row.plReconciliationTotal))}
            </span>
          )}
          {row.excluded && (
            <span
              className="sa-ppl-chip sa-ppl-chip-excluded"
              title={row.excludedReason ? `Excluded: ${row.excludedReason}` : 'Excluded from dashboard metrics'}
            >
              <EyeOff size={11} /> Excluded
            </span>
          )}
          {!readOnly && (
            <button
              type="button"
              className={row.excluded ? 'sa-ppl-row-finalize' : 'sa-ppl-feed-exclude'}
              disabled={busy}
              title={row.excluded ? 'Put this project back in P&L totals and the default list' : 'Hide from KPIs and overhead allocation'}
              onClick={handleToggleExcluded}
            >
              {row.excluded ? <Eye size={11} /> : <EyeOff size={11} />}
              {busy
                ? (row.excluded ? 'Re-including…' : 'Excluding…')
                : (row.excluded ? 'Re-include' : 'Exclude')}
            </button>
          )}
          {!row.pmFinalizedAt && !row.excluded && !readOnly && (
            <button
              type="button"
              className="sa-ppl-row-finalize"
              disabled={busy}
              onClick={handleFinalize}
            >
              {busy ? 'Finalizing…' : 'Finalize'}
            </button>
          )}
          {row.pmFinalizedAt && onReopen && !readOnly && (
            <button
              type="button"
              className="sa-ppl-row-finalize"
              disabled={busy}
              onClick={handleReopen}
            >
              {busy ? 'Reopening…' : 'Reopen'}
            </button>
          )}
        </span>
      </div>

      {expanded && (
        <div className="sa-ppl-row-body">
          <ProjectPLBudgetPanel
            row={row}
            formatCurrencyFull={formatCurrencyFull}
            readOnly={readOnly}
            onExpenseBucketChange={onExpenseBucketChange}
            onReassignPayment={onReassignPayment}
            onReassignExpense={onReassignExpense}
            reassignableDeals={reassignableDeals}
          />
        </div>
      )}
    </div>
  );
};

export default ProjectPLRow;
